/**
 * src/components/spatial/spatialChatModel.ts
 *
 * The state a private conversation is in, as a pure function of what the
 * server said.
 *
 * Kept out of the panel because every rule worth checking lives here: a
 * greeting seeds the transcript, a reply appends both halves of the turn,
 * a refusal becomes a sentence without losing the transcript, and some
 * refusals mean the session is gone rather than merely unhappy. Those are
 * decisions, not markup, and testing them through a renderer would test
 * the renderer.
 */

import { retryText } from './spatialBlockerText';
import type { SpatialInteractionSession } from '../../services/spatial/spatialPresenceClient';

export type ChatTurn = { who: 'object' | 'you'; text: string; at: string };

export interface ChatState {
  turns: ChatTurn[];
  /** Messages the server will still accept in this session. */
  remaining: number;
  closed: boolean;
  /** The most recent refusal as a sentence, or null. */
  error: string | null;
}

/** What `sendSpatialMessage` resolves to, narrowed to what state cares about. */
export type SendOutcome =
  | { ok: true; reply: string; remainingMessages: number }
  | { ok: false; reason: string; message?: string; retryAfterMs?: number };

/**
 * Refusals that mean there is nothing left to type into. Everything else
 * is a rejected message in a session that is still open — a message cap
 * or a rate limit does not end a conversation.
 */
const TERMINAL_REASONS = new Set(['session_not_found', 'object_not_found', 'object_not_interactive']);

export function seedChat(session: SpatialInteractionSession): ChatState {
  return {
    turns: session.greeting
      ? [{ who: 'object', text: session.greeting, at: session.startedAt }]
      : [],
    remaining: session.remainingMessages,
    closed: session.status !== 'open',
    error: null,
  };
}

/**
 * Fold one exchange in.
 *
 * On refusal the sent text is deliberately not appended: it never
 * reached the object, and showing it as said would misreport what
 * happened.
 */
export function applySend(state: ChatState, sent: string, outcome: SendOutcome, at: string): ChatState {
  if (!outcome.ok) {
    return {
      ...state,
      // With its timing when the server gave one: a rationed refusal
      // that does not say when to come back is a dead end.
      error: retryText(outcome),
      closed: state.closed || TERMINAL_REASONS.has(outcome.reason),
    };
  }
  return {
    turns: [
      ...state.turns,
      { who: 'you', text: sent, at },
      { who: 'object', text: outcome.reply, at },
    ],
    remaining: outcome.remainingMessages,
    closed: state.closed,
    error: null,
  };
}

/**
 * Fold in an exchange the server answered without the object.
 *
 * A shortcut chip asks something the spatial services can answer
 * directly — what can be done here, what an action needs — and the
 * reply is read from that answer rather than generated. It belongs in
 * the transcript, because from the visitor's side they asked and were
 * answered, and a panel that showed it anywhere else would be two
 * conversations in one card.
 *
 * `remaining` is deliberately untouched. The ration exists to bound
 * what the visitor costs us in model calls, and this cost none — so
 * spending one here would charge them for the cheap path and push them
 * back towards the expensive one.
 */
export function applyAside(
  state: ChatState,
  asked: string,
  answered: string,
  at: string,
): ChatState {
  return {
    ...state,
    turns: [
      ...state.turns,
      { who: 'you', text: asked, at },
      { who: 'object', text: answered, at },
    ],
    error: null,
  };
}

/** Closing is unconditional: a session the operator has left is left. */
export function applyEnd(state: ChatState): ChatState {
  return { ...state, closed: true, error: null };
}

/** Whether the composer should accept input. */
export function canSend(state: ChatState, draft: string, busy: boolean): boolean {
  return !busy && !state.closed && state.remaining > 0 && draft.trim().length > 0;
}
